<script setup lang="ts">
import { ActionButton, FileUpload, StatusTag } from '@taler/ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{
  hasPhoto: boolean
  isUploading: boolean
  isRemoving: boolean
  isBusy: boolean
  errorMessage: string | undefined
}>()
const emit = defineEmits<{ upload: [file: File]; remove: [] }>()

const { t } = useI18n()
const file = ref<File | null>(null)

function submit(): void {
  if (file.value === null) return
  emit('upload', file.value)
}
</script>

<template>
  <section
    class="flex flex-col gap-4 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4"
  >
    <form class="flex flex-col gap-4" @submit.prevent="submit">
      <FileUpload
        v-model="file"
        :label="t('garage.photo.title')"
        accept="image/jpeg,image/png,image/webp"
        :accept-hint="t('garage.photo.acceptHint')"
        :browse-label="t('garage.photo.browse')"
        :drop-hint="t('garage.photo.dropHint')"
        :remove-label="t('garage.photo.removeFile')"
        :invalid-type-message="t('garage.photo.invalidFileType')"
        :disabled="isBusy"
      />
      <div class="flex flex-wrap items-center gap-2">
        <ActionButton type="submit" :disabled="file === null || isBusy">
          {{
            isUploading
              ? t('garage.photo.uploading')
              : hasPhoto
                ? t('garage.photo.replace')
                : t('garage.photo.upload')
          }}
        </ActionButton>
        <button
          v-if="hasPhoto"
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] px-3 py-2 text-[color:var(--color-danger)] hover:bg-[color:var(--color-surface-raised)]"
          :disabled="isBusy"
          @click="emit('remove')"
        >
          {{ isRemoving ? t('garage.photo.removing') : t('garage.photo.remove') }}
        </button>
      </div>
      <StatusTag v-if="errorMessage !== undefined" tone="danger" role="alert">
        {{ errorMessage }}
      </StatusTag>
    </form>
  </section>
</template>
